import { useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import {
  BedDoubleIcon,
  CarIcon,
  CheckIcon,
  ChevronDownIcon,
  CopyIcon,
  ExternalLinkIcon,
  MapPinIcon,
  PackageIcon,
  PlaneIcon,
  PlusIcon,
  UsersIcon,
  XIcon,
} from 'lucide-react';
import { Avatar, Button, DatePicker, ErrorNotice, Field, LoadingState, Panel, Pill, SelectInput, TextArea, TextInput, type PillTone, useDebouncedValue } from '@bcecloud/portal-ui';
import { errorMessage } from '@/lib/api';
import { cn } from '@/lib/utils';
import {
  LOGISTICS_SECTIONS,
  avatarSrc,
  listEventLogistics,
  listUsers,
  personName,
  readMyIdentity,
  saveEventLogistics,
  type EventPerson,
  type LogisticsForm,
  type LogisticsSection,
  type LogisticsSectionData,
  type LogisticsSubmission,
} from '@/lib/events';
import {
  bookingLink,
  brandLogoUrl,
  brandsFor,
  formatDateRange,
  matchBrand,
  nightsBetween,
  type TravelBrandKind,
} from '@/lib/travel-brands';

/**
 * The Logistics tab — per-traveler flight/hotel/rental/supplies tracking.
 *
 * Every traveler has their own five-section board. Contractors see and edit
 * ONLY their own (travel details are personal); internal staff see every
 * traveler, switch between them, add new travelers, and edit anyone's board
 * — production manages the whole travel picture from here.
 */

/** Roles that manage every traveler's logistics. */
const MANAGER_ROLES = new Set(['admin', 'superadmin', 'internal']);

/** Display config per section. */
const SECTION_META: Record<
  LogisticsSection,
  {
    title: string;
    question: string;
    icon: React.ComponentType<{ className?: string; 'aria-hidden'?: boolean }>;
    brandKind?: TravelBrandKind;
  }
> = {
  supplies: {
    title: 'Supplies',
    question: 'Will you need supplies from the Best Warehouse?',
    icon: PackageIcon,
  },
  assistants: {
    title: 'Assistants',
    question: 'Will you need assistants for this event?',
    icon: UsersIcon,
  },
  flight: {
    title: 'Flight',
    question: 'Will you need a flight for this event?',
    icon: PlaneIcon,
    brandKind: 'airline',
  },
  hotel: {
    title: 'Hotel',
    question: 'Will you need a hotel for this event?',
    icon: BedDoubleIcon,
    brandKind: 'hotel',
  },
  rentalCar: {
    title: 'Rental car',
    question: 'Will you need a rental car for this event?',
    icon: CarIcon,
    brandKind: 'rentalCar',
  },
};

/** Travel sections derive their status from the confirmation number. */
const TRAVEL_SECTIONS = new Set<LogisticsSection>(['flight', 'hotel', 'rentalCar']);

/** The at-a-glance state of one section. */
function sectionState(
  section: LogisticsSection,
  data: LogisticsSectionData | undefined,
): { label: string; tone: PillTone } {
  if (!data?.isNeeded) return { label: 'Unanswered', tone: 'neutral' };
  if (data.isNeeded === 'no') return { label: 'Not needed', tone: 'neutral' };
  if (TRAVEL_SECTIONS.has(section)) {
    // Booked means there's a confirmation number — no manual status to forget.
    return data.confirmationNumber?.trim()
      ? { label: 'Booked', tone: 'success' }
      : { label: 'Pending', tone: 'danger' };
  }
  if (data.status === 'shipped') return { label: 'Shipped', tone: 'warning' };
  if (data.status === 'booked' || data.status === 'received') {
    return { label: data.status === 'booked' ? 'Booked' : 'Received', tone: 'success' };
  }
  return { label: 'Pending', tone: 'danger' };
}

export function LogisticsTab({ eventId }: { readonly eventId: string }): React.ReactElement {
  const queryClient = useQueryClient();
  const me = useQuery({ queryKey: ['staff-me'], queryFn: ({ signal }) => readMyIdentity(signal) });
  const forms = useQuery({
    queryKey: ['event-logistics', eventId],
    queryFn: ({ signal }) => listEventLogistics(eventId, signal),
  });
  const [activeTraveler, setActiveTraveler] = useState<string | undefined>(undefined);

  if (me.isPending || forms.isPending) return <LoadingState label="Loading logistics…" />;
  if (forms.isError) {
    return <ErrorNotice title="Logistics unavailable" message={errorMessage(forms.error)} />;
  }

  const myId = me.data?.id ?? '';
  const isManager = MANAGER_ROLES.has(me.data?.role ?? '');
  const travelerId = activeTraveler ?? myId;
  const activeForm = forms.data.find(
    (form) => typeof form.facilitator === 'object' && form.facilitator._id === travelerId,
  );
  const activePerson =
    activeForm && typeof activeForm.facilitator === 'object'
      ? activeForm.facilitator
      : travelerId === myId
        ? { _id: myId, firstName: me.data?.firstName, lastName: me.data?.lastName }
        : undefined;

  async function refresh(): Promise<void> {
    await queryClient.invalidateQueries({ queryKey: ['event-logistics', eventId] });
  }

  return (
    <div className="mt-6">
      {isManager ? (
        <TravelerStrip
          eventId={eventId}
          forms={forms.data}
          myId={myId}
          activeTravelerId={travelerId}
          onSelect={setActiveTraveler}
          onAdded={async (id) => {
            await refresh();
            setActiveTraveler(id);
          }}
        />
      ) : null}

      <LogisticsEditor
        key={`${travelerId}:${activeForm?._id ?? 'new'}`}
        eventId={eventId}
        existing={activeForm}
        travelerId={travelerId}
        travelerLastName={activePerson?.lastName}
        isSelf={travelerId === myId}
        onSaved={refresh}
      />
    </div>
  );
}

/**
 * The traveler switcher for internal staff: one chip per traveler with their
 * avatar, plus an add-traveler lookup so production can track anyone who's
 * flying — facilitators, assistants, staff.
 */
function TravelerStrip({
  eventId,
  forms,
  myId,
  activeTravelerId,
  onSelect,
  onAdded,
}: {
  readonly eventId: string;
  readonly forms: readonly LogisticsForm[];
  readonly myId: string;
  readonly activeTravelerId: string;
  readonly onSelect: (travelerId: string) => void;
  readonly onAdded: (travelerId: string) => Promise<void>;
}): React.ReactElement {
  const [adding, setAdding] = useState(false);
  const [query, setQuery] = useState('');
  const debounced = useDebouncedValue(query);
  const results = useQuery({
    queryKey: ['user-lookup', debounced],
    queryFn: ({ signal }) => listUsers({ page: 1, search: debounced.trim() }, signal),
    enabled: adding && debounced.trim().length >= 2,
    staleTime: 60_000,
  });
  const add = useMutation({
    mutationFn: (userId: string) => saveEventLogistics(eventId, {}, userId),
    onSuccess: async (form) => {
      setAdding(false);
      setQuery('');
      const id = typeof form.facilitator === 'object' ? form.facilitator._id : form.facilitator;
      await onAdded(id);
    },
  });

  const travelers = forms
    .map((form) => (typeof form.facilitator === 'object' ? form.facilitator : undefined))
    .filter((person): person is EventPerson => person !== undefined);
  const travelerIds = new Set(travelers.map((person) => person._id));
  // The signed-in manager can always open their own board, form or not.
  const includesMe = travelerIds.has(myId);
  const suggestions = (results.data?.people ?? []).filter(
    (person) => person.role !== 'client' && !travelerIds.has(person._id),
  );

  return (
    <div className="mb-4">
      <div className="flex flex-wrap items-center gap-2">
        {!includesMe ? (
          <TravelerChip
            label="You"
            active={activeTravelerId === myId}
            onClick={() => onSelect(myId)}
          />
        ) : null}
        {travelers.map((person) => (
          <button
            key={person._id}
            type="button"
            onClick={() => onSelect(person._id)}
            className={cn(
              'flex items-center gap-2 rounded-portal-pill border py-1 pr-3 pl-1 transition-colors',
              activeTravelerId === person._id
                ? 'border-brand bg-portal-sky'
                : 'border-portal-line bg-white hover:border-brand/50',
            )}
          >
            <Avatar name={personName(person)} size={26} imageUrl={avatarSrc(person)} />
            <span className="text-portal-small font-bold">
              {personName(person)}
              {person._id === myId ? ' (you)' : ''}
            </span>
          </button>
        ))}

        {adding ? null : (
          <button
            type="button"
            onClick={() => setAdding(true)}
            className="flex items-center gap-1 rounded-portal-pill border border-dashed border-portal-line px-3 py-1.5 text-portal-small font-bold text-portal-muted transition-colors hover:border-brand hover:text-brand"
          >
            <PlusIcon aria-hidden className="size-4" /> Add traveler
          </button>
        )}
      </div>

      {adding ? (
        <div className="relative mt-2 max-w-sm">
          <TextInput
            autoFocus
            placeholder="Search people to add as a traveler…"
            aria-label="Search people to add as a traveler"
            value={query}
            onChange={(event) => setQuery(event.target.value)}
          />
          {debounced.trim().length >= 2 ? (
            <div className="absolute z-20 mt-1 w-full overflow-hidden rounded-portal-button border border-portal-line bg-white shadow-portal-card">
              {suggestions.map((person) => (
                <button
                  key={person._id}
                  type="button"
                  disabled={add.isPending}
                  onClick={() => add.mutate(person._id)}
                  className="flex w-full items-center gap-2.5 px-3 py-2 text-left hover:bg-portal-bg"
                >
                  <Avatar name={personName(person)} size={28} imageUrl={avatarSrc(person)} />
                  <span className="min-w-0">
                    <span className="block truncate text-portal-small font-bold">
                      {personName(person)}
                    </span>
                    <span className="block truncate text-portal-micro text-portal-muted">
                      {person.email}
                    </span>
                  </span>
                </button>
              ))}
              {suggestions.length === 0 && !results.isPending ? (
                <p className="px-3 py-2 text-portal-micro text-portal-muted">
                  No matching people.
                </p>
              ) : null}
            </div>
          ) : null}
          <button
            type="button"
            onClick={() => {
              setAdding(false);
              setQuery('');
            }}
            className="mt-1.5 text-portal-micro font-bold text-portal-muted hover:text-portal-ink"
          >
            Cancel
          </button>
          {add.isError ? (
            <p className="mt-1 text-portal-micro font-semibold text-rose-600">
              {errorMessage(add.error)}
            </p>
          ) : null}
        </div>
      ) : null}
    </div>
  );
}

/** A plain traveler chip without an avatar (the "You" placeholder). */
function TravelerChip({
  label,
  active,
  onClick,
}: {
  readonly label: string;
  readonly active: boolean;
  readonly onClick: () => void;
}): React.ReactElement {
  return (
    <button
      type="button"
      onClick={onClick}
      className={cn(
        'rounded-portal-pill border px-3 py-1.5 text-portal-small font-bold transition-colors',
        active ? 'border-brand bg-portal-sky' : 'border-portal-line bg-white hover:border-brand/50',
      )}
    >
      {label}
    </button>
  );
}

/** One traveler's editable board: section tiles as sub-tabs. */
function LogisticsEditor({
  eventId,
  existing,
  travelerId,
  travelerLastName,
  isSelf,
  onSaved,
}: {
  readonly eventId: string;
  readonly existing: LogisticsForm | undefined;
  readonly travelerId: string;
  readonly travelerLastName: string | undefined;
  readonly isSelf: boolean;
  readonly onSaved: () => Promise<void>;
}): React.ReactElement {
  const [submission, setSubmission] = useState<LogisticsSubmission>(existing?.submission ?? {});
  const [active, setActive] = useState<LogisticsSection>('supplies');
  const [saved, setSaved] = useState(false);

  const save = useMutation({
    mutationFn: () => saveEventLogistics(eventId, submission, isSelf ? undefined : travelerId),
    onSuccess: async () => {
      setSaved(true);
      await onSaved();
    },
  });

  function update(section: LogisticsSection, patch: Partial<LogisticsSectionData>): void {
    setSaved(false);
    setSubmission((prev) => {
      const merged = { ...prev[section], ...patch };
      // Travel status derives from the confirmation number; keep the stored
      // status in step so legacy readers and the read views agree.
      if (TRAVEL_SECTIONS.has(section)) {
        merged.status = merged.confirmationNumber?.trim() ? 'booked' : 'pending';
      }
      return { ...prev, [section]: merged };
    });
  }

  const dirty = JSON.stringify(submission) !== JSON.stringify(existing?.submission ?? {});

  return (
    <div>
      {/* Section tiles double as sub-tabs. */}
      <div
        role="tablist"
        aria-label="Logistics sections"
        className="mb-4 grid grid-cols-2 gap-2 sm:grid-cols-3 xl:grid-cols-5"
      >
        {LOGISTICS_SECTIONS.map((section) => {
          const meta = SECTION_META[section];
          const state = sectionState(section, submission[section]);
          const selected = active === section;
          const Icon = meta.icon;
          return (
            <button
              key={section}
              type="button"
              role="tab"
              aria-selected={selected}
              onClick={() => setActive(section)}
              className={cn(
                'rounded-portal-button border bg-white px-3 py-2 text-left transition-colors',
                selected
                  ? 'border-brand shadow-portal-card'
                  : 'border-portal-line hover:border-brand/50',
              )}
            >
              <span className="flex items-center gap-1.5 text-portal-micro font-extrabold tracking-[0.6px] text-portal-muted uppercase">
                <Icon aria-hidden className={cn('size-3.5', selected && 'text-brand')} />
                {meta.title}
              </span>
              <span className="mt-1 block">
                <Pill tone={state.tone}>{state.label}</Pill>
              </span>
            </button>
          );
        })}
      </div>

      <SectionPanel
        key={active}
        section={active}
        data={submission[active] ?? {}}
        travelerLastName={travelerLastName}
        onChange={(patch) => update(active, patch)}
      />

      {save.isError ? (
        <div className="mt-4">
          <ErrorNotice inline title="Not saved" message={errorMessage(save.error)} />
        </div>
      ) : null}

      <div className="mt-4 flex items-center gap-3">
        <Button disabled={save.isPending || !dirty} onClick={() => save.mutate()}>
          {save.isPending ? 'Saving…' : 'Save logistics'}
        </Button>
        {saved && !dirty ? (
          <span className="text-portal-micro font-bold text-emerald-600">Saved</span>
        ) : null}
        {dirty ? (
          <span className="text-portal-micro text-portal-muted">Unsaved changes</span>
        ) : null}
      </div>
    </div>
  );
}

/** A dropdown of known brands with their logos, plus free-text entry. */
function BrandSelect({
  id,
  kind,
  value,
  onChange,
  placeholder,
}: {
  readonly id: string;
  readonly kind: TravelBrandKind;
  readonly value: string;
  readonly onChange: (value: string) => void;
  readonly placeholder: string;
}): React.ReactElement {
  const [open, setOpen] = useState(false);
  const [custom, setCustom] = useState('');
  const brands = brandsFor(kind);
  const selected = matchBrand(value, kind);

  return (
    <div className="relative">
      <button
        type="button"
        id={id}
        aria-haspopup="listbox"
        aria-expanded={open}
        onClick={() => setOpen((state) => !state)}
        className={cn(
          'mt-1.5 flex h-[43px] w-full items-center gap-2 rounded-portal-input border border-[#cad3df] bg-white px-3 text-left text-portal-small font-normal',
          !value && 'text-portal-muted',
        )}
      >
        {selected ? (
          <img
            src={brandLogoUrl(selected)}
            alt=""
            className="size-5 shrink-0 rounded object-contain"
            onError={(event) => {
              event.currentTarget.style.display = 'none';
            }}
          />
        ) : null}
        <span className="min-w-0 flex-1 truncate">{value || placeholder}</span>
        <ChevronDownIcon aria-hidden className="size-4 shrink-0 text-portal-muted" />
      </button>

      {open ? (
        <div
          role="listbox"
          className="absolute z-20 mt-1 max-h-72 w-full overflow-y-auto rounded-portal-button border border-portal-line bg-white shadow-portal-card"
        >
          {brands.map((brand) => (
            <button
              key={brand.name}
              type="button"
              role="option"
              aria-selected={value === brand.name}
              onClick={() => {
                onChange(brand.name);
                setOpen(false);
              }}
              className={cn(
                'flex w-full items-center gap-2.5 px-3 py-2 text-left text-portal-small hover:bg-portal-bg',
                value === brand.name && 'bg-portal-sky',
              )}
            >
              <img
                src={brandLogoUrl(brand)}
                alt=""
                className="size-5 rounded object-contain"
                onError={(event) => {
                  event.currentTarget.style.display = 'none';
                }}
              />
              <span className="flex-1">{brand.name}</span>
              {value === brand.name ? (
                <CheckIcon aria-hidden className="size-4 text-brand" />
              ) : null}
            </button>
          ))}
          <div className="border-t border-portal-line p-2">
            <TextInput
              density="compact"
              placeholder="Other — type a name…"
              aria-label="Other brand name"
              value={custom}
              onChange={(event) => setCustom(event.target.value)}
              onKeyDown={(event) => {
                if (event.key === 'Enter' && custom.trim()) {
                  event.preventDefault();
                  onChange(custom.trim());
                  setCustom('');
                  setOpen(false);
                }
              }}
            />
          </div>
          {value ? (
            <button
              type="button"
              onClick={() => {
                onChange('');
                setOpen(false);
              }}
              className="flex w-full items-center gap-1.5 border-t border-portal-line px-3 py-2 text-left text-portal-micro font-bold text-portal-muted hover:bg-portal-bg"
            >
              <XIcon aria-hidden className="size-3.5" /> Clear
            </button>
          ) : null}
        </div>
      ) : null}
    </div>
  );
}

/**
 * The enriched booking summary: brand logo, dates, confirmation with a copy
 * button, map link, and a deep link to the booking (confirmation + traveler
 * last name pre-filled where the brand's manage page accepts them).
 */
function BookingCard({
  section,
  data,
  travelerLastName,
}: {
  readonly section: LogisticsSection;
  readonly data: LogisticsSectionData;
  readonly travelerLastName: string | undefined;
}): React.ReactElement | null {
  const [copied, setCopied] = useState(false);
  const meta = SECTION_META[section];
  if (!meta.brandKind) return null;
  const brandSource = section === 'flight' ? data.airline : data.company;
  if (!brandSource && !data.confirmationNumber) return null;

  const brand = matchBrand(brandSource, meta.brandKind);
  const range = formatDateRange(data.checkIn, data.checkOut);
  const nights = section === 'hotel' ? nightsBetween(data.checkIn, data.checkOut) : undefined;
  const address = [
    data.address?.street,
    data.address?.city,
    data.address?.state,
    data.address?.zip,
  ]
    .filter(Boolean)
    .join(', ');
  const state = sectionState(section, data);
  const link = bookingLink(brand, data.confirmationNumber, travelerLastName);
  const Icon = meta.icon;

  return (
    <div className="mt-4 overflow-hidden rounded-portal-card border border-portal-line bg-portal-bg/60">
      <div className="flex items-center gap-3 p-4">
        {brand ? (
          <img
            src={brandLogoUrl(brand)}
            alt=""
            className="size-10 shrink-0 rounded-portal-button border border-portal-line bg-white object-contain p-1.5"
            onError={(event) => {
              event.currentTarget.style.display = 'none';
            }}
          />
        ) : (
          <span className="flex size-10 shrink-0 items-center justify-center rounded-portal-button border border-portal-line bg-white text-portal-muted">
            <Icon aria-hidden className="size-5" />
          </span>
        )}
        <div className="min-w-0 flex-1">
          <p className="truncate text-portal-small font-bold text-portal-ink">
            {brandSource || brand?.name || meta.title}
          </p>
          <p className="text-portal-micro text-portal-muted">
            {range ?? 'Dates not set'}
            {nights ? ` · ${nights} night${nights === 1 ? '' : 's'}` : ''}
          </p>
        </div>
        <Pill tone={state.tone}>{state.label}</Pill>
      </div>

      <div className="flex flex-wrap items-center gap-2 border-t border-portal-line bg-white px-4 py-2.5">
        {data.confirmationNumber ? (
          <button
            type="button"
            onClick={() => {
              navigator.clipboard.writeText(data.confirmationNumber ?? '').catch(() => {});
              setCopied(true);
              window.setTimeout(() => setCopied(false), 1500);
            }}
            className="flex items-center gap-1.5 rounded-portal-button border border-portal-line px-2 py-1 text-portal-micro font-bold text-portal-ink transition-colors hover:border-brand"
            aria-label={`Copy confirmation number ${data.confirmationNumber}`}
          >
            <span className="font-mono">{data.confirmationNumber}</span>
            {copied ? (
              <CheckIcon aria-hidden className="size-3.5 text-emerald-600" />
            ) : (
              <CopyIcon aria-hidden className="size-3.5 text-portal-muted" />
            )}
          </button>
        ) : null}
        <span className="flex-1" />
        {address ? (
          <a
            href={`https://www.google.com/maps/search/?api=1&query=${encodeURIComponent(address)}`}
            target="_blank"
            rel="noreferrer noopener"
            className="flex items-center gap-1 rounded-portal-button px-2 py-1 text-portal-micro font-bold text-brand-link hover:bg-portal-bg"
          >
            <MapPinIcon aria-hidden className="size-3.5" /> Map
          </a>
        ) : null}
        {link ? (
          <a
            href={link}
            target="_blank"
            rel="noreferrer noopener"
            title="Copies the confirmation number as it opens, ready to paste"
            onClick={() => {
              // Prefill-by-URL isn't reliable across carriers (verified:
              // their lookup pages ignore query params), so make paste the
              // one-step fallback: the confirmation rides the clipboard.
              if (data.confirmationNumber) {
                navigator.clipboard.writeText(data.confirmationNumber).catch(() => {});
                setCopied(true);
                window.setTimeout(() => setCopied(false), 1500);
              }
            }}
            className="flex items-center gap-1.5 rounded-portal-button bg-brand px-2.5 py-1 text-portal-micro font-bold text-white transition-opacity hover:opacity-90"
          >
            <ExternalLinkIcon aria-hidden className="size-3.5" /> Open booking
          </a>
        ) : null}
      </div>
    </div>
  );
}

/** One editable section: the needed? question, then conditional details. */
function SectionPanel({
  section,
  data,
  travelerLastName,
  onChange,
}: {
  readonly section: LogisticsSection;
  readonly data: LogisticsSectionData;
  readonly travelerLastName: string | undefined;
  readonly onChange: (patch: Partial<LogisticsSectionData>) => void;
}): React.ReactElement {
  const meta = SECTION_META[section];
  const needed = data.isNeeded === 'yes';
  const showStatusSelect = needed && !TRAVEL_SECTIONS.has(section);

  return (
    <Panel className="p-5">
      <div className="grid gap-4 sm:grid-cols-2">
        <Field label={meta.question} htmlFor={`${section}-needed`}>
          <SelectInput
            id={`${section}-needed`}
            value={data.isNeeded ?? ''}
            onChange={(event) =>
              onChange({ isNeeded: event.target.value as LogisticsSectionData['isNeeded'] })
            }
          >
            <option value="">Not answered</option>
            <option value="yes">Yes</option>
            <option value="no">No</option>
          </SelectInput>
        </Field>
        {showStatusSelect ? (
          <Field label="Status" htmlFor={`${section}-status`}>
            <SelectInput
              id={`${section}-status`}
              value={data.status ?? 'pending'}
              onChange={(event) => onChange({ status: event.target.value })}
            >
              {(section === 'supplies' ? ['pending', 'shipped', 'received'] : ['pending', 'booked']).map(
                (status) => (
                  <option key={status} value={status}>
                    {status[0]?.toUpperCase()}
                    {status.slice(1)}
                  </option>
                ),
              )}
            </SelectInput>
          </Field>
        ) : null}
      </div>

      {needed ? (
        <BookingCard section={section} data={data} travelerLastName={travelerLastName} />
      ) : null}
      {needed ? <SectionDetails section={section} data={data} onChange={onChange} /> : null}

      <div className="mt-4">
        <Field label="Comments" htmlFor={`${section}-comments`}>
          <TextArea
            id={`${section}-comments`}
            rows={2}
            placeholder="Anything production or the traveler should know…"
            value={data.comments ?? ''}
            onChange={(event) => onChange({ comments: event.target.value })}
          />
        </Field>
      </div>
    </Panel>
  );
}

/** The section-specific detail fields, shown once the section is needed. */
function SectionDetails({
  section,
  data,
  onChange,
}: {
  readonly section: LogisticsSection;
  readonly data: LogisticsSectionData;
  readonly onChange: (patch: Partial<LogisticsSectionData>) => void;
}): React.ReactElement | null {
  if (section === 'supplies') {
    const items = data.items ?? [];
    return (
      <div className="mt-4">
        <p className="text-portal-tiny font-bold">Items</p>
        <div className="mt-2 space-y-2">
          {items.map((item, index) => (
            // Positional edits of a small primitive list — index keys are fine.
            // eslint-disable-next-line react/no-array-index-key
            <div key={index} className="flex items-center gap-2">
              <TextInput
                placeholder="Item name, e.g. Bike toolkit"
                aria-label={`Item ${index + 1} name`}
                value={item.name}
                onChange={(event) =>
                  onChange({
                    items: items.map((it, i) =>
                      i === index ? { ...it, name: event.target.value } : it,
                    ),
                  })
                }
              />
              <TextInput
                placeholder="Qty"
                aria-label={`Item ${index + 1} quantity`}
                type="number"
                className="w-24"
                value={item.quantity ?? ''}
                onChange={(event) =>
                  onChange({
                    items: items.map((it, i) =>
                      i === index
                        ? { ...it, quantity: Number(event.target.value) || undefined }
                        : it,
                    ),
                  })
                }
              />
              <button
                type="button"
                aria-label={`Remove item ${item.name || index + 1}`}
                onClick={() => onChange({ items: items.filter((_, i) => i !== index) })}
                className="shrink-0 rounded-portal-button p-1.5 text-portal-muted transition-colors hover:bg-portal-bg hover:text-rose-600"
              >
                <XIcon aria-hidden className="size-4" />
              </button>
            </div>
          ))}
        </div>
        <button
          type="button"
          onClick={() => onChange({ items: [...items, { name: '' }] })}
          className="mt-2 flex items-center gap-1 text-portal-micro font-bold text-brand hover:underline"
        >
          <PlusIcon aria-hidden className="size-3.5" /> Add item
        </button>
      </div>
    );
  }

  if (section === 'assistants') {
    return (
      <div className="mt-4 sm:max-w-[240px]">
        <Field label="How many assistants will you need?" htmlFor="assistants-count">
          <TextInput
            id="assistants-count"
            type="number"
            min={0}
            placeholder="e.g. 2"
            value={data.count ?? ''}
            onChange={(event) => onChange({ count: Number(event.target.value) || undefined })}
          />
        </Field>
      </div>
    );
  }

  if (section === 'flight') {
    return (
      <div className="mt-4 grid gap-4 sm:grid-cols-2">
        <Field label="Airline" htmlFor="flight-airline">
          <BrandSelect
            id="flight-airline"
            kind="airline"
            value={data.airline ?? ''}
            onChange={(airline) => onChange({ airline })}
            placeholder="Choose an airline…"
          />
        </Field>
        <Field label="Confirmation number" htmlFor="flight-confirmation">
          <TextInput
            id="flight-confirmation"
            placeholder="e.g. FJFBGY — marks the flight booked"
            value={data.confirmationNumber ?? ''}
            onChange={(event) => onChange({ confirmationNumber: event.target.value })}
          />
        </Field>
      </div>
    );
  }

  // Hotel and rental car share company/confirmation/address/dates.
  const isHotel = section === 'hotel';
  const address = data.address ?? {};
  const setAddress = (patch: Partial<typeof address>): void =>
    onChange({ address: { ...address, ...patch } });
  return (
    <div className="mt-4 space-y-4">
      <div className="grid gap-4 sm:grid-cols-2">
        <Field label={isHotel ? 'Hotel name' : 'Company'} htmlFor={`${section}-company`}>
          {isHotel ? (
            <TextInput
              id={`${section}-company`}
              placeholder="e.g. Omni Las Colinas"
              value={data.company ?? ''}
              onChange={(event) => onChange({ company: event.target.value })}
            />
          ) : (
            <BrandSelect
              id={`${section}-company`}
              kind="rentalCar"
              value={data.company ?? ''}
              onChange={(company) => onChange({ company })}
              placeholder="Choose a company…"
            />
          )}
        </Field>
        <Field label="Confirmation number" htmlFor={`${section}-confirmation`}>
          <TextInput
            id={`${section}-confirmation`}
            placeholder={`e.g. ${isHotel ? 'HTL-88421' : 'RES-10422'} — marks it booked`}
            value={data.confirmationNumber ?? ''}
            onChange={(event) => onChange({ confirmationNumber: event.target.value })}
          />
        </Field>
      </div>
      <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-4">
        <Field label="Street" htmlFor={`${section}-street`}>
          <TextInput
            id={`${section}-street`}
            placeholder="221 E Las Colinas Blvd"
            value={address.street ?? ''}
            onChange={(event) => setAddress({ street: event.target.value })}
          />
        </Field>
        <Field label="City" htmlFor={`${section}-city`}>
          <TextInput
            id={`${section}-city`}
            placeholder="Irving"
            value={address.city ?? ''}
            onChange={(event) => setAddress({ city: event.target.value })}
          />
        </Field>
        <Field label="State" htmlFor={`${section}-state`}>
          <TextInput
            id={`${section}-state`}
            placeholder="TX"
            value={address.state ?? ''}
            onChange={(event) => setAddress({ state: event.target.value })}
          />
        </Field>
        <Field label="Zip" htmlFor={`${section}-zip`}>
          <TextInput
            id={`${section}-zip`}
            placeholder="75039"
            value={address.zip ?? ''}
            onChange={(event) => setAddress({ zip: event.target.value })}
          />
        </Field>
      </div>
      <div className="grid gap-4 sm:max-w-[420px] sm:grid-cols-2">
        <Field label={isHotel ? 'Check in' : 'Pick up'} htmlFor={`${section}-checkin`}>
          <DatePicker
            id={`${section}-checkin`}
            value={data.checkIn}
            placeholder={isHotel ? 'Check-in date' : 'Pick-up date'}
            onChange={(checkIn) => onChange({ checkIn })}
          />
        </Field>
        <Field label={isHotel ? 'Check out' : 'Drop off'} htmlFor={`${section}-checkout`}>
          <DatePicker
            id={`${section}-checkout`}
            value={data.checkOut}
            placeholder={isHotel ? 'Check-out date' : 'Drop-off date'}
            onChange={(checkOut) => onChange({ checkOut })}
          />
        </Field>
      </div>
    </div>
  );
}
